class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Use event delegation on the persistent cart-drawer element so listeners
    // survive section re-renders that replace inner DOM elements.
    this.addEventListener('click', this.handleDrawerClick.bind(this));

    // Listen for global cart update events (from qty change, delete, etc.)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (this.classList.contains('active')) {
          // Wait for DOM to settle after the update
          setTimeout(() => this.fetchRecommendations(), 250);
        }
      });
    }
  }

  handleDrawerClick(e) {
    if (e.target.closest('#CartDrawer-NoteTrigger')) {
      this.openBottomPanel('note');
    } else if (e.target.closest('#CartDrawer-DiscountTrigger')) {
      this.openBottomPanel('discount');
    } else if (e.target.closest('#BottomUpPanel-Close')) {
      this.closeBottomPanel();
    } else if (e.target.closest('#CartDrawer-ApplyDiscount')) {
      this.applyDiscount();
    } else if (e.target.closest('#BottomUp-SaveNote')) {
      this.saveNote();
    } else if (e.target.closest('.cart-drawer__discount-remove')) {
      const btn = e.target.closest('.cart-drawer__discount-remove');
      this.removeDiscount(btn.dataset.discountCode);
    }
  }

  // initBottomPanel kept for renderContents() compatibility — no longer binds clicks.
  initBottomPanel() {}

  openBottomPanel(type) {
    const panel = this.querySelector('#CartDrawer-BottomUpPanel');
    const title = this.querySelector('#BottomUpPanel-Title');
    const noteContent = this.querySelector('#BottomUp-Note');
    const discountContent = this.querySelector('#BottomUp-Discount');

    if (type === 'note') {
      title.textContent = 'Add order note';
      noteContent.classList.remove('hidden');
      discountContent.classList.add('hidden');
    } else {
      title.textContent = 'Add discount code';
      noteContent.classList.add('hidden');
      discountContent.classList.remove('hidden');
    }

    panel.classList.add('active');
  }

  closeBottomPanel() {
    const panel = this.querySelector('#CartDrawer-BottomUpPanel');
    if (panel) panel.classList.remove('active');
  }

  applyDiscount() {
    const input = this.querySelector('#CartDrawer-DiscountCode');
    const code = input ? input.value.trim() : '';
    if (!code) return;

    this.closeBottomPanel();

    // Use /cart/update.js with bundled section rendering so we get
    // the discount applied AND updated section HTML in one request.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: code,
        sections: 'cart-drawer,cart-icon-bubble',
      }),
    })
      .then((res) => res.json())
      .then((data) => this.applyCartSections(data))
      .catch(() => this.refreshDrawerSection());
  }

  removeDiscount(code) {
    // Per Shopify Cart API docs: POST /cart/update.js with discount:''
    // removes all applied discount codes from the cart.
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: '',
        sections: 'cart-drawer,cart-icon-bubble',
      }),
    })
      .then((res) => res.json())
      .then((data) => this.applyCartSections(data))
      .catch(() => this.refreshDrawerSection());
  }

  // Parse and inject section HTML returned by bundled section rendering.
  applyCartSections(data) {
    if (data.sections && data.sections['cart-drawer']) {
      const root = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
      const newInner = root.querySelector('.drawer__inner');
      const currentInner = this.querySelector('.drawer__inner');
      if (newInner && currentInner) {
        currentInner.innerHTML = newInner.innerHTML;
      }
    }

    if (data.sections && data.sections['cart-icon-bubble']) {
      const root = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
      const currentBubble = document.getElementById('cart-icon-bubble');
      const newBubble = root.querySelector('.shopify-section');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }
    }

    // Sync is-empty state
    const itemCount = data.item_count ?? (data.items ? data.items.length : null);
    if (itemCount !== null) {
      if (itemCount === 0) {
        this.classList.add('is-empty');
      } else {
        this.classList.remove('is-empty');
      }
    }

    // Re-fetch recommendations since inner HTML was replaced
    this.fetchRecommendations();
  }

  // Fallback: re-fetches the section via Section Rendering API when
  // bundled rendering isn't available (e.g. on note save or manual drawer refresh).
  refreshDrawerSection() {
    const sectionUrl = `/cart?section_id=cart-drawer&_cb=${Date.now()}`;

    return fetch(sectionUrl, { cache: 'no-store' })
      .then((res) => res.text())
      .then((text) => {
        const root = new DOMParser().parseFromString(text, 'text/html');
        const newInner = root.querySelector('.drawer__inner');
        const currentInner = this.querySelector('.drawer__inner');

        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
        }

        const newBubble = root.querySelector('#cart-icon-bubble .shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }

        this.fetchRecommendations();
      });
  }

  saveNote() {
    const textarea = this.querySelector('#CartDrawer-Note');
    const note = textarea ? textarea.value : '';
    const body = JSON.stringify({ note });
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() => {
      this.closeBottomPanel();
      this.updateDrawer();
    });
  }

  updateDrawer() {
    const cartItems = this.querySelector('cart-drawer-items');
    if (cartItems) {
      cartItems.onCartUpdate();
    }
    // Re-fetch recommendations on cart update
    this.fetchRecommendations();
  }

  fetchRecommendations() {
    const sidebar = this.querySelector('#CartDrawer-Recommendations');
    const mobileContainer = this.querySelector('#CartDrawer-RecommendationsMobile');
    if (!sidebar && !mobileContainer) return;

    console.log('[CartDrawer] fetchRecommendations triggered');

    fetch(`${window.routes.cart_url}.js?_=${Date.now()}`)
      .then((response) => response.json())
      .then((cart) => {
        console.log('[CartDrawer] Cart state:', cart);
        if (cart && cart.item_count > 0) {
          const productId = cart.items[0].product_id;
          const url = `/recommendations/products?section_id=cart-drawer-recommendations&product_id=${productId}&limit=4`;
          console.log('[CartDrawer] Fetching recommendations for product:', productId);

          fetch(url)
            .then((response) => response.text())
            .then((text) => {
              const root = new DOMParser().parseFromString(text, 'text/html');
              const container = root.querySelector('.cart-drawer-recommendations');
              
              if (container && container.innerHTML.trim().length > 0) {
                console.log('[CartDrawer] Recommendations found, showing widget');
                const html = container.innerHTML;
                if (sidebar) {
                  sidebar.innerHTML = html;
                  sidebar.classList.add('active');
                }
                if (mobileContainer) {
                  mobileContainer.innerHTML = html;
                  mobileContainer.classList.add('active');
                }
                this.initRecommendationEvents();
              } else {
                console.log('[CartDrawer] No recommendations returned from API');
                if (sidebar) sidebar.classList.remove('active');
                if (mobileContainer) mobileContainer.classList.remove('active');
              }
            });
        } else {
          console.log('[CartDrawer] Cart is empty, hiding widgets');
          if (sidebar) sidebar.classList.remove('active');
          if (mobileContainer) mobileContainer.classList.remove('active');
        }
      });
  }

  initRecommendationEvents() {
    this.querySelectorAll('.cart-recommendation-item__quick-view').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const productUrl = btn.dataset.productUrl;
        this.openQuickView(productUrl);
      });
    });
  }

  openQuickView(productUrl) {
    // Create quick view overlay if it doesn't exist
    let quickView = this.querySelector('#CartDrawer-QuickView');
    if (!quickView) {
      quickView = document.createElement('div');
      quickView.id = 'CartDrawer-QuickView';
      quickView.className = 'cart-drawer__quick-view-panel';
      this.querySelector('.drawer__inner').appendChild(quickView);
    }

    // Fetch product section
    const fetchUrl = productUrl.includes('?') ? `${productUrl}&section_id=quick-view-content` : `${productUrl}?section_id=quick-view-content`;
    fetch(fetchUrl)
      .then((response) => response.text())
      .then((text) => {
        const root = new DOMParser().parseFromString(text, 'text/html');
        const section = root.querySelector('.shopify-section');
        
        quickView.innerHTML = `
          <div class="quick-view__header">
            <span class="quick-view__title">SELECT OPTIONS</span>
            <button type="button" class="quick-view__close" aria-label="Close Quick View">
               <span class="svg-wrapper">${this.querySelector('.drawer__close .svg-wrapper').innerHTML}</span>
            </button>
          </div>
          <div class="quick-view__content">${section ? section.innerHTML : 'Product details not found.'}</div>
        `;
        quickView.classList.add('active');
        
        quickView.querySelector('.quick-view__close').addEventListener('click', () => {
          quickView.classList.remove('active');
        });

        // Initialize any functionality in the quick view content
        this.initQuickViewContent(quickView);
      });
  }

  initQuickViewContent(container) {
    const form = container.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const productInfo = container.querySelector('.quick-view-product');
    if (!productInfo) return;

    const productId = productInfo.dataset.productId;
    const variantsJsonElement = document.getElementById(`QuickView-VariantsJson-${productId}`);
    if (!variantsJsonElement) return;

    const variants = JSON.parse(variantsJsonElement.textContent);
    const idInput = form.querySelector('input[name="id"]');
    const priceDisplay = container.querySelector('.quick-view-product__price');
    const submitButton = form.querySelector('[name="add"]');
    const submitButtonText = submitButton ? submitButton.querySelector('span') : null;

    // Handle Variant selection
    container.addEventListener('change', (e) => {
      if (e.target.classList.contains('quick-view-variant-radio')) {
        const selectedOptions = Array.from(form.querySelectorAll('.quick-view-variant-radio:checked')).map(input => input.value);
        console.log('[QuickView] Options changed:', selectedOptions);

        const matchedVariant = variants.find(variant => {
          return variant.options.every((option, index) => option === selectedOptions[index]);
        });

        if (matchedVariant && idInput) {
          console.log('[QuickView] Matched Variant:', matchedVariant.id, matchedVariant.title);
          idInput.value = matchedVariant.id;

          if (priceDisplay) {
            const basePrice = (matchedVariant.price / 100).toFixed(2).replace('.', ',');
            priceDisplay.textContent = `€ ${basePrice}`;
          }

          if (submitButton && submitButtonText) {
            if (matchedVariant.available) {
              submitButton.disabled = false;
              submitButtonText.textContent = 'ADD TO CART';
            } else {
              submitButton.disabled = true;
              submitButtonText.textContent = 'SOLD OUT';
            }
          }
        }
      }
    });

    // Handle quantity buttons
    const quantityInput = form.querySelector('.quick-view-quantity-field');
    container.addEventListener('click', (e) => {
      if (e.target.classList.contains('quick-view-quantity-btn')) {
        const action = e.target.dataset.action;
        let currentVal = parseInt(quantityInput.value) || 1;
        if (action === 'plus') quantityInput.value = currentVal + 1;
        if (action === 'minus' && currentVal > 1) quantityInput.value = currentVal - 1;
      }
    });

    // Handle AJAX add to cart in quick view
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        console.log('[QuickView] Submitting Form...');
        for (let [key, value] of formData.entries()) {
          console.log(`[QuickView] Form Data - ${key}:`, value);
        }

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        })
        .then(response => response.json())
        .then((data) => {
          console.log('[QuickView] Add to cart success:', data);
          container.classList.remove('active');
          this.updateDrawer();
        });
      });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.fetchRecommendations();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.initBottomPanel();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

customElements.whenDefined('cart-items').then(() => {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
});
